/**
 * A popover that escapes its container. Rendered at the top of the document and
 * positioned against its trigger, so no card's overflow, transform or stacking
 * context can clip it — which is exactly what was happening to the note tagger
 * inside the ticket panel, where only three of the names fitted and the rest
 * were cut off.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export function AnchoredPop({ anchor, onClose, children, width = 220, className = '' }: {
  /** CSS selector of the trigger, resolved from this popover's own placeholder. */
  anchor: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
  className?: string;
}) {
  const mark = useRef<HTMLSpanElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; up: boolean } | null>(null);

  useLayoutEffect(() => {
    const trigger = mark.current?.parentElement?.querySelector(anchor) as HTMLElement | null;
    const r = (trigger || mark.current)?.getBoundingClientRect();
    if (!r) return;
    const max = 280;
    const room = window.innerHeight - r.bottom;
    setPos({
      top: room < max ? Math.max(8, r.top - Math.min(max, r.top - 8) - 8) : r.bottom + 8,
      left: Math.min(Math.max(8, r.left), window.innerWidth - width - 8),
      up: room < max,
    });
  }, [anchor, width]);

  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (pop.current?.contains(e.target as Node)) return;
      if (mark.current?.parentElement?.contains(e.target as Node)) return;
      onClose();
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    // Closing on scroll keeps the popover glued to its trigger — but the
    // listener is in the capture phase, so scrolling the popover's OWN list
    // closed it too. That is why you could never reach the fourth colleague
    // down: the list shut the moment you tried to scroll it.
    const scrolled = (e: Event) => {
      if (pop.current?.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', scrolled, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', scrolled, true);
    };
  }, [onClose]);

  return (
    <>
      <span ref={mark} style={{ display: 'none' }} />
      {pos && createPortal(
        <div
          ref={pop}
          className={`anchored-pop ${className}`}
          style={{ top: pos.top, left: pos.left, width }}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}
